import { useEffect, useState } from 'react';
import { Users, Plus, Loader2, Trash2, Clock, CheckCircle, XCircle, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';

const GENRES = [
  'House', 'Techno', 'Tech House', 'Deep House', 'Progressive House',
  'Melodic Techno', 'Afro House', 'Minimal', 'Trance', 'Drum & Bass',
  'Reggaeton', 'Latin', 'Cumbia', 'Pop', 'Open Format', 'Otro',
];

const emptyForm = {
  artistic_name: '', real_name: '', bio: '', genres: [], city: '',
  phone: '', email_contact: '', instagram: '', experience_years: '',
};

const StatusPill = ({ status }) => {
  const cfg = {
    pending: { c: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30', Icon: Clock, t: 'Pendiente' },
    approved: { c: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', Icon: CheckCircle, t: 'Aprobado' },
    rejected: { c: 'bg-red-500/15 text-red-400 border-red-500/30', Icon: XCircle, t: 'Rechazado' },
  }[status] || { c: 'bg-zinc-700 text-zinc-300 border-zinc-600', Icon: Clock, t: status };
  const { c, Icon, t } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${c}`}>
      <Icon size={10} /> {t}
    </span>
  );
};

// Agency roster: agencies create & manage their DJs (each goes through admin approval)
export default function AgencyRosterSection({ onDjsLoaded, onEditDj }) {
  const [djs, setDjs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchDjs = () => {
    api.get('/dj/agency/my-djs')
      .then((res) => {
        const list = res.data?.djs || [];
        setDjs(list);
        onDjsLoaded?.(list.filter((d) => d.status === 'approved'));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDjs(); }, []);

  const toggleGenre = (g) => {
    setForm((f) => ({
      ...f,
      genres: f.genres.includes(g) ? f.genres.filter((x) => x !== g) : [...f.genres, g],
    }));
  };

  const handleCreate = async () => {
    if (form.genres.length === 0) { toast.error('Seleccioná al menos un género'); return; }
    if ((form.bio || '').length < 50) { toast.error('La bio debe tener al menos 50 caracteres'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/dj/agency/my-djs', {
        ...form,
        experience_years: form.experience_years ? Number(form.experience_years) : null,
      });
      toast.success(res.data?.message || 'DJ agregado');
      setShowModal(false);
      setForm(emptyForm);
      fetchDjs();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Error al agregar DJ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (dj) => {
    if (!window.confirm(`¿Eliminar a ${dj.artistic_name} de tu roster?`)) return;
    try {
      await api.delete(`/dj/agency/my-djs/${dj.id}`);
      toast.success('DJ eliminado');
      fetchDjs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  return (
    <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 mb-6" data-testid="agency-roster-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <Users size={20} className="text-[#D4AF37]" />
          Mis DJs
          <span className="text-xs font-normal text-zinc-500">({djs.length})</span>
        </h3>
        <Button
          onClick={() => setShowModal(true)}
          size="sm"
          className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
          data-testid="agency-add-dj-btn"
        >
          <Plus size={14} className="mr-1" /> Agregar DJ
        </Button>
      </div>

      {loading ? (
        <Loader2 size={20} className="animate-spin text-[#D4AF37]" />
      ) : djs.length === 0 ? (
        <p className="text-sm text-zinc-500">Todavía no agregaste DJs a tu agencia. Cada DJ que agregues pasa por la aprobación del equipo de Bravo y aparece en el directorio público.</p>
      ) : (
        <div className="space-y-3">
          {djs.map((dj) => (
            <div key={dj.id} className="flex items-center gap-3 p-3 bg-[#18181B] rounded-xl" data-testid={`agency-dj-${dj.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#27272A] flex items-center justify-center overflow-hidden flex-shrink-0">
                {dj.photo_url ? <img src={dj.photo_url} alt="" className="w-full h-full object-cover" /> : <Users size={18} className="text-[#D4AF37]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-sm truncate">{dj.artistic_name}</p>
                  <StatusPill status={dj.status} />
                </div>
                <p className="text-[11px] text-zinc-500 truncate">{dj.city} · {(dj.genres || []).slice(0, 3).join(', ')}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEditDj?.(dj)}
                className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-3 text-xs font-bold flex-shrink-0"
                data-testid={`agency-dj-edit-${dj.id}`}
              >
                <Pencil size={12} className="mr-1" /> Editar perfil
              </Button>
              {dj.status !== 'approved' && (
                <button onClick={() => handleDelete(dj)} className="text-red-400/60 hover:text-red-400 p-1" data-testid={`agency-dj-delete-${dj.id}`}>
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Agregar DJ a tu agencia</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Nombre artístico *</Label>
                <Input value={form.artistic_name} onChange={(e) => setForm({ ...form, artistic_name: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" data-testid="agency-dj-artistic-name" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Nombre real *</Label>
                <Input value={form.real_name} onChange={(e) => setForm({ ...form, real_name: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Ciudad *</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Teléfono *</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Email de contacto</Label>
                <Input type="email" value={form.email_contact} onChange={(e) => setForm({ ...form, email_contact: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Instagram</Label>
                <Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@usuario" className="bg-zinc-800 border-zinc-700 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Bio * (mín. 50 caracteres)</Label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="bg-zinc-800 border-zinc-700 mt-1" data-testid="agency-dj-bio" />
              <p className={`text-[10px] mt-1 ${form.bio.length >= 50 ? 'text-emerald-400' : 'text-zinc-500'}`}>{form.bio.length}/50</p>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Géneros *</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {GENRES.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => toggleGenre(g)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${form.genres.includes(g) ? 'bg-[#D4AF37] text-black' : 'bg-[#27272A] text-zinc-400 hover:bg-[#3F3F46]'}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Años de experiencia</Label>
              <Input type="number" min={0} max={50} value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1 max-w-[120px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button
              onClick={handleCreate}
              disabled={submitting || !form.artistic_name || !form.real_name || !form.city || !form.phone}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
              data-testid="agency-dj-submit"
            >
              {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Enviar para aprobación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
